import * as React from 'react';
import { useOpenAIGlobal } from 'react-openai-apps-sdk';
import { DialogContent as BaseDialogContent } from './dialog';
import { cn } from '@/lib/utils';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * DialogContent wrapper that respects safe area insets in fullscreen mode.
 * Use this instead of DialogContent when you need safe area support.
 *
 * In inline/PiP mode: Uses 95% viewport width for better space utilization.
 * In fullscreen mode: Respects safe area insets and constrains to app max-width.
 *
 * @example
 * ```tsx
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <DialogContentSafeArea>
 *     <DialogHeader>...</DialogHeader>
 *     ...
 *   </DialogContentSafeArea>
 * </Dialog>
 * ```
 */
export const DialogContentSafeArea = React.forwardRef<
  React.ElementRef<typeof BaseDialogContent>,
  React.ComponentPropsWithoutRef<typeof BaseDialogContent>
>(({ className, style, children, ...props }, ref) => {
  const displayMode = useOpenAIGlobal('displayMode') as string | undefined;
  const safeArea = useOpenAIGlobal('safeArea') as { insets: SafeAreaInsets } | undefined;

  const insets = safeArea?.insets || { top: 0, bottom: 0, left: 0, right: 0 };
  const isFullscreen = displayMode === 'fullscreen';

  // Apply constraints based on display mode
  // - Fullscreen: Use inline styles for safe area calculations (dynamic values)
  // - Inline/PiP: Use className overrides for max-width
  const safeAreaStyle = isFullscreen
    ? {
        ...style,
        // Adjust positioning to account for safe areas
        top: `calc(50% + ${(insets.top - insets.bottom) / 2}px)`,
        // Constrain max height to avoid overflow into safe areas
        maxHeight: `calc(95vh - ${insets.top + insets.bottom}px)`,
        // Constrain max width with safe area insets, but cap at app max-width (--container-6xl)
        maxWidth: `min(var(--container-6xl), calc(100vw - ${insets.left + insets.right + 32}px))`,
      }
    : {
        ...style,
        // Inline/PiP: Limit max height
        maxHeight: '85vh',
      };

  return (
    <BaseDialogContent
      ref={ref}
      className={cn(
        // In inline/PiP mode, override the default max-w-lg (512px) to use 95% of viewport width
        !isFullscreen && 'max-w-[95vw]',
        className
      )}
      style={safeAreaStyle}
      {...props}
    >
      {children}
    </BaseDialogContent>
  );
});

DialogContentSafeArea.displayName = 'DialogContentSafeArea';
